import { definedMap, hasOwnProperty } from "collection-utils";
import last from "lodash/last";
import type {
    ActionPropertyDescriptorCase,
    AppDescriptionContext,
    ColumnPropertyDescriptorCase,
    ColumnTypePredicate,
    EditedColumnsAndTables,
    EnumPropertyCase,
    EnumPropertyDescriptorCase,
    GetAllowedColumnsFunction,
    MultiCasePropertyDescriptor,
    NumberPropertyDescriptorCase,
    PropertyDescriptor,
    PropertyDescriptorCase,
    PropertyLabel,
    PropertyTableGetter,
    SchemaInspector,
    SpecialValuePropertyDescriptorCase,
    StringPropertyDescriptorCase,
    StringyColumnPropertyFlag,
    Subcomponent,
} from ".";
import {
    combineEditedColumnsAndTables,
    componentErrorAndLinkForReference,
    emptyEditedColumnsAndTables,
    getPropertyDescription,
    makeStringyColumnPropertyDescriptor,
    RequiredKind,
} from ".";
import type { BasePrimitiveValue } from "../../components/computation-types";
import { getUserProfileTableInfo } from "../../components/user-profile";
import { assert, checkString, defined, removeArrayItem, replaceArrayItem } from "../../Support";
import type {
    ActionDescription,
    ArrayFilter,
    ColumnAssignment,
    ColumnTypeKind,
    Description,
    FormScreenDescription,
    InputOutputTables,
    PropertyDescription,
    PropertySource,
    SourceColumn,
    TableAndColumn,
    TableColumn,
    TableGlideType,
} from "../description";
import {
    getBackendCompatiblePrimitiveColumns,
    getInputOrOutputTable,
    getPrimitiveColumns,
    getPrimitiveNonHiddenColumns,
    getPrimitiveOrPrimitiveArrayNonHiddenColumns,
    getStringTypeOrStringTypeArrayColumns,
    getTableColumn,
    getTableColumnDisplayName,
    ImageKind,
    isColumnNonHidden,
    isColumnWriteable,
    isDateTimeTypeKind,
    isMultiRelationType,
    isNumberTypeKind,
    isSingleRelationType,
    MutatingScreenKind,
    PropertyKind,
    sheetNameForTable,
} from "../description";
import {
    doFilterColumnsExist,
    getColumnAssignments,
    getColumnNameAndGroup,
    getPropertyAndDescriptorFromDescription,
    getPropertyDescriptorCaseForDescription,
    getPropertyDescriptorCaseForKind,
    imageProperties,
    isImageType,
    titleProperties,
} from "../description-utils";
import { resolveSourceColumn } from "../source-column";
import { handlerForPropertyKind } from "./description-handlers";
import { ColumnPropertyFlag, ColumnPropertyHandler } from "./property-handlers";
import type { SuperPropertySection } from "./property-sections";
import { PropertySection } from "./property-sections";
import {
    getArrayProperty,
    getColumnProperty,
    getSourceColumnProperty,
    getTableProperty,
    makeEnumProperty,
} from "./property-utils";

function getColumnPropertyDescription<T extends Description>(
    pd: PropertyDescriptor,
    desc: T
): { sourceColumn: SourceColumn; columnDescr: ColumnPropertyDescriptorCase<T> } | undefined {
    const propertyDesc = getPropertyDescription(desc, pd);
    if (propertyDesc === undefined) return undefined;
    const sourceColumn = getSourceColumnProperty(propertyDesc);
    if (sourceColumn === undefined) return undefined;
    const columnDescr = getPropertyDescriptorCaseForDescription(propertyDesc, pd) as ColumnPropertyDescriptorCase<T>;
    if (columnDescr === undefined) return undefined;

    return { sourceColumn, columnDescr };
}

interface ColumnWithDescriptorCase<T extends Description> extends TableAndColumn {
    readonly columnDescr: ColumnPropertyDescriptorCase<T>;
}

export function getColumnForProperty<T extends Description>(
    pd: PropertyDescriptor,
    desc: T,
    tables: InputOutputTables,
    ccc: AppDescriptionContext
): ColumnWithDescriptorCase<T> | undefined {
    const propertyDesc = getColumnPropertyDescription(pd, desc);
    if (propertyDesc === undefined) return undefined;
    const { columnDescr, sourceColumn } = propertyDesc;

    const table = getInputOrOutputTable(tables, columnDescr.isEditedInApp === true);
    let propertyTable: TableGlideType | undefined;
    if (columnDescr.getIndirectTable === undefined) {
        propertyTable = table;
    } else {
        if (ccc === undefined) return undefined;
        propertyTable = columnDescr.getIndirectTable(tables, desc, ccc);
    }

    const resolved = definedMap(resolveSourceColumn(ccc, sourceColumn, propertyTable, undefined), last);
    if (resolved === undefined) return undefined;

    return { ...resolved, columnDescr };
}

// This will only return columns used from the context, not, for example,
// columns used from the user profile row.
export function getColumnsUsedInDescription<T extends Description>(
    propertyDescriptors: ReadonlyArray<PropertyDescriptor>,
    desc: T,
    tables: InputOutputTables,
    ccc: AppDescriptionContext,
    predicate?: (column: TableColumn, columnDescr: ColumnPropertyDescriptorCase<T>) => boolean
): ReadonlyArray<TableColumn> {
    const columns: TableColumn[] = [];
    for (const pd of propertyDescriptors) {
        const column = getColumnForProperty(pd, desc, tables, ccc);
        if (column !== undefined) {
            if (column.table !== tables.input && column.table !== tables.output) continue;
            if (predicate !== undefined && !predicate(column.column, column.columnDescr)) continue;
            columns.push(column.column);
            continue;
        }

        const propertyDesc = getPropertyDescription(desc, pd);
        if (propertyDesc === undefined) continue;

        const descrCase = getPropertyDescriptorCaseForDescription(propertyDesc, pd);

        if (descrCase?.kind === PropertyKind.Array) {
            const arr = getArrayProperty<T>(propertyDesc);
            if (arr === undefined) continue;
            for (const item of arr) {
                columns.push(...getColumnsUsedInDescription(descrCase.properties, item, tables, ccc, predicate));
            }
        }
    }
    return columns;
}

export function getInputOrOutputTableForProperty<T extends Description>(
    tables: InputOutputTables,
    pdc: ColumnPropertyDescriptorCase<T>
): TableGlideType {
    return getInputOrOutputTable(tables, pdc.isEditedInApp === true);
}

export function rewriteFilter(filter: ArrayFilter, table: TableGlideType): ArrayFilter | undefined {
    if (doFilterColumnsExist(filter, table) === true) {
        return filter;
    }
    return undefined;
}

export function getDefaultCaptionColumnDescriptor<T extends Description>(
    propertyDescriptors: ReadonlyArray<PropertyDescriptor>
): { descr: PropertyDescriptor; pdc: ColumnPropertyDescriptorCase<T> } | undefined {
    for (const descr of propertyDescriptors) {
        const pdc = getPropertyDescriptorCaseForKind<T>(PropertyKind.Column, descr);

        if (pdc === undefined || pdc.kind !== PropertyKind.Column) continue;
        if (pdc.isDefaultCaption !== true) continue;

        return { descr, pdc };
    }
    return undefined;
}

export function getDefaultCaption<T extends Description>(
    propertyDescriptors: ReadonlyArray<PropertyDescriptor>,
    desc: T,
    tables: InputOutputTables | undefined,
    schema: SchemaInspector
): string | undefined {
    const defaultCaptionDescriptor = getDefaultCaptionColumnDescriptor(propertyDescriptors);
    if (defaultCaptionDescriptor === undefined) return undefined;
    const { descr, pdc } = defaultCaptionDescriptor;

    const pd = getPropertyDescription(desc, descr);
    if (pd?.kind === PropertyKind.Column && pdc.kind === PropertyKind.Column) {
        assert(pdc.kind === PropertyKind.Column);
        if (tables === undefined) return undefined;
        const sourceColumn = getSourceColumnProperty(pd);
        if (sourceColumn === undefined) return undefined;
        const columnName = checkString(sourceColumn.name);
        const table = getInputOrOutputTable(tables, pdc.isEditedInApp === true);
        const column = getTableColumn(table, columnName);
        if (column === undefined) return columnName;
        return getColumnNameAndGroup(column)[1];
    } else if (pd?.kind === PropertyKind.Table) {
        const tableName = getTableProperty(pd);
        if (tableName === undefined) return undefined;
        const table = schema.findTable(tableName);
        if (table === undefined) return undefined;
        return sheetNameForTable(table);
    } else {
        return undefined;
    }
}

const imageKindCases: ReadonlyArray<EnumPropertyCase<ImageKind>> = [
    { value: ImageKind.URL, label: "URL" },
    { value: ImageKind.MapFromAddress, label: "Map from address" },
];

export function makeTitlePropertyDescriptor<T extends Description>(
    getPropertyTable: PropertyTableGetter<T> | undefined,
    required: boolean,
    isEditedInApp: boolean
): PropertyDescriptor {
    return makeStringyColumnPropertyDescriptor(
        "titleProperty",
        "Title",
        [
            required ? "required" : "optional",
            "editable",
            "searchable",
            ...(isEditedInApp ? ["edited-in-app" as const] : []),
        ],
        PropertySection.Data,
        getPropertyTable,
        titleProperties
    );
}

export function makeSubtitlePropertyDescriptor<T extends Description>(
    getPropertyTable: PropertyTableGetter<T> | undefined
): PropertyDescriptor {
    return makeStringyColumnPropertyDescriptor(
        "subtitleProperty",
        "Details",
        ["editable", "searchable"],
        PropertySection.Data,
        getPropertyTable,
        titleProperties,
        ["titleProperty"]
    );
}

export function makeSummaryImagePropertyDescriptor<T extends Description>(
    getPropertyTable: PropertyTableGetter<T> | undefined,
    imageRequired: boolean,
    isEditedInApp: boolean,
    withEmoji: boolean
): PropertyDescriptor {
    return {
        kind: PropertyKind.Column,
        property: { name: "imageURLProperty" },
        label: "Image",
        required: imageRequired,
        editable: true,
        searchable: false,
        isEditedInApp,
        getIndirectTable: getPropertyTable,
        getAllowedColumns: getStringTypeOrStringTypeArrayColumns,
        preferredNames: imageProperties,
        preferredType: t => isImageType(t, withEmoji, true),
        section: PropertySection.Data,
    };
}

export function makeSummaryImageKindPropertyDescriptor(imageKind: ImageKind | undefined): PropertyDescriptor {
    if (imageKind === undefined) {
        return {
            kind: PropertyKind.Enum,
            property: { name: "imageKind" },
            label: "Image is",
            menuLabel: "Image source",
            cases: imageKindCases,
            defaultCaseValue: ImageKind.URL,
            section: PropertySection.Design,
            visual: "dropdown",
            isSearchable: false,
        };
    } else {
        return {
            kind: PropertyKind.Constant,
            property: { name: "imageKind" },
            // This isn't used
            label: "Image is",
            value: imageKind,
            doNotRewrite: true,
            section: PropertySection.Hidden,
        };
    }
}

export function makeSummaryPropertyDescriptors<T extends Description>(
    titleRequired: boolean,
    includeDescription: boolean,
    // if `imageRequired` is `undefined`, don't include an image
    imageRequired: boolean | undefined,
    imageKind: ImageKind | undefined,
    getPropertyTable: PropertyTableGetter<T> | undefined,
    withEmoji: boolean,
    imageFirst: boolean,
    additionalDescriptorsBeforeImage: readonly PropertyDescriptor[] = []
): readonly PropertyDescriptor[] {
    const descrs: PropertyDescriptor[] = [makeTitlePropertyDescriptor(getPropertyTable, titleRequired, false)];

    if (includeDescription) {
        descrs.push(makeSubtitlePropertyDescriptor(getPropertyTable));
    }
    if (imageFirst) {
        descrs.unshift(...additionalDescriptorsBeforeImage);
    } else {
        descrs.push(...additionalDescriptorsBeforeImage);
    }

    if (imageRequired !== undefined) {
        const imageDescriptor = makeSummaryImagePropertyDescriptor(getPropertyTable, imageRequired, false, withEmoji);
        const imageKindDescriptor = makeSummaryImageKindPropertyDescriptor(imageKind);

        if (imageFirst) {
            descrs.unshift(imageDescriptor);
        } else {
            descrs.push(imageDescriptor);
        }

        descrs.push(imageKindDescriptor);
    }

    return descrs;
}

export function makeCaptionPropertyDescriptor<T extends Description>(
    getPropertyTable: PropertyTableGetter<T> | undefined,
    emptyByDefault: boolean
): PropertyDescriptor {
    const flags: StringyColumnPropertyFlag[] = ["editable", "searchable"];
    if (emptyByDefault) {
        flags.push("empty-by-default");
    }

    return makeStringyColumnPropertyDescriptor(
        "captionProperty",
        "Caption",
        flags,
        PropertySection.Data,
        getPropertyTable,
        ["caption", ...titleProperties],
        ["titleProperty", "subtitleProperty"]
    );
}

const primaryKeyPropertyName = "primaryKeyProperty";

export function makePrimaryKeyPropertyHandler(
    ccc: AppDescriptionContext,
    table: TableGlideType | undefined,
    alwaysEnabled: boolean = false
): readonly ColumnPropertyHandler<Description>[] {
    if (table?.rowIDColumn !== undefined) return [];

    const flags = [ColumnPropertyFlag.Editable, ColumnPropertyFlag.EmptyByDefault];
    if (!alwaysEnabled && !ccc.userFeatures.primaryKeyProperties) {
        flags.push(ColumnPropertyFlag.Legacy);
    }
    return [
        new ColumnPropertyHandler(
            primaryKeyPropertyName,
            "Key",
            flags,
            undefined,
            ["key", "id", "primary key", "identifier"],
            getBackendCompatiblePrimitiveColumns,
            "string",
            PropertySection.Data
        ),
    ];
}

export function getPrimaryKeyProperty(desc: Description): string | undefined {
    if (!hasOwnProperty(desc, primaryKeyPropertyName)) return undefined;
    return getColumnProperty(desc[primaryKeyPropertyName] as PropertyDescription);
}

export const useFallbackInitialsPropertyDescriptor: PropertyDescriptor = {
    kind: PropertyKind.Switch,
    property: { name: "useFallbackInitials" },
    label: "Show title when image is not available",
    defaultValue: true,
    section: PropertySection.Options,
};

export function makePropertyDescriptorsForColumns<TDesc extends Description>(
    tables: InputOutputTables,
    getColumns: (desc: TDesc) => readonly ColumnAssignment[],
    makeUpdate: (columns: readonly ColumnAssignment[]) => Partial<TDesc>,
    {
        withActionSource,
        withClearColumn,
        allowUserSpecificColumns,
        allowCustomAndUserProfile,
        emptyByDefault,
        propertySection = PropertySection.Data,
        excludeOutputColumns,
    }: {
        withActionSource: boolean;
        withClearColumn: boolean;
        allowUserSpecificColumns: boolean;
        allowCustomAndUserProfile: boolean;
        emptyByDefault: boolean;
        propertySection?: SuperPropertySection;
        excludeOutputColumns?: ReadonlySet<string>;
    }
): [readonly PropertyDescriptor[], readonly TableColumn[]] {
    const { input: inputTable, output: destTable } = tables;

    const writeableColumns =
        definedMap(destTable, t =>
            t.columns.filter(
                c =>
                    isColumnWriteable(c, destTable, allowUserSpecificColumns) &&
                    excludeOutputColumns?.has(c.name) !== true
            )
        ) ?? [];
    const withColumnCase = getPrimitiveNonHiddenColumns(inputTable).length > 0;
    const descrs = writeableColumns.map(c => {
        const cases: PropertyDescriptorCase<TDesc>[] = [];

        if (withColumnCase) {
            cases.push({
                kind: PropertyKind.Column,
                required: false,
                editable: true,
                searchable: false,
                emptyByDefault,
                allowUserProfileColumns: allowCustomAndUserProfile,
                getAllowedColumns: getPrimitiveNonHiddenColumns,
                preferredNames: [c.name],
                preferredType: c.type.kind,
            });
        }
        cases.push({
            kind: PropertyKind.SpecialValue,
            withActionSource,
            withClearColumn,
        });
        if (allowCustomAndUserProfile) {
            cases.push({
                kind: PropertyKind.String,
                required: false,
                placeholder: "Custom",
            });
        }

        function findAssignment(d: TDesc): [ColumnAssignment, number] | undefined {
            const columns = getColumns(d);
            const index = columns.findIndex(a => a.destColumn === c.name);
            if (index < 0) return undefined;
            return [columns[index], index];
        }

        return {
            label: getTableColumnDisplayName(c),
            property: {
                id: c.name,
                get: d => definedMap(findAssignment(d), ([a]) => a.value),
                update: (d, v) => {
                    let columns = getColumns(d);
                    const index = definedMap(findAssignment(d), ([, i]) => i);
                    if (v !== undefined) {
                        const newAssignment = { destColumn: c.name, value: v };
                        if (index !== undefined) {
                            columns = replaceArrayItem(columns, index, newAssignment);
                        } else {
                            columns = [...columns, newAssignment];
                        }
                    } else if (index !== undefined) {
                        columns = removeArrayItem(columns, index);
                    }
                    return makeUpdate(columns);
                },
            } as PropertySource<TDesc>,
            cases,
            section: propertySection,
            writesToColumn: { table: destTable, column: c },
        } as MultiCasePropertyDescriptor<any>;
    });
    return [descrs, writeableColumns];
}

export function makePropertyDescriptorsForFormScreen(
    tables: InputOutputTables,
    withActionSource: boolean
): readonly PropertyDescriptor[] {
    return makePropertyDescriptorsForColumns<FormScreenDescription>(
        tables,
        getColumnAssignments,
        cs => ({
            columnAssignments: cs,
        }),
        {
            withActionSource: withActionSource,
            withClearColumn: false,
            allowUserSpecificColumns: false,
            allowCustomAndUserProfile: false,
            emptyByDefault: false,
        }
    )[0];
}

export const screenTitlePropertyDescriptor: PropertyDescriptor = {
    property: { name: "title" },
    label: "Title",
    section: PropertySection.Title,
    cases: [
        {
            kind: PropertyKind.Column,
            required: false,
            editable: true,
            searchable: false,
            getAllowedColumns: getPrimitiveColumns,
            preferredNames: ["title", "name"],
            preferredType: "string",
        },
        {
            kind: PropertyKind.String,
            required: false,
            placeholder: "Screen title",
        },
    ],
};

function makeColumnOrLiteralPropertyDescriptor<T extends Description, P extends BasePrimitiveValue>(
    property: string | PropertySource<T>,
    label: PropertyLabel,
    mutatingScreenKind: MutatingScreenKind | undefined,
    propertySection: SuperPropertySection,
    subcomponent: Subcomponent | undefined,
    columnFirst: boolean,
    columnCase: ColumnPropertyDescriptorCase<T> | undefined,
    otherCases: readonly (
        | StringPropertyDescriptorCase
        | NumberPropertyDescriptorCase
        | EnumPropertyDescriptorCase<P, T>
        | SpecialValuePropertyDescriptorCase
    )[]
): PropertyDescriptor | undefined {
    if (typeof property === "string") {
        property = { name: property };
    }

    const propertyDescriptorBase = {
        property,
        label,
        section: propertySection,
        subcomponent,
    };

    // The image property in the Image component needs this clause.  It's edited
    // in the app, so in the Add screen it has to have a column picker, too.
    // FIXME: This is really a hack, where we special-cased the Image component,
    // and then we special-cased the default value case.
    if (
        columnCase === undefined ||
        (columnCase.needsExistingValue !== true &&
            mutatingScreenKind === MutatingScreenKind.AddScreen &&
            columnCase.isEditedInApp !== true)
    ) {
        if (otherCases.length === 0) {
            return undefined;
        }

        if (otherCases.length === 1) {
            const descr: PropertyDescriptor = {
                ...propertyDescriptorBase,
                ...otherCases[0],
            };
            return descr;
        } else {
            return {
                ...propertyDescriptorBase,
                cases: otherCases,
            };
        }
    }

    if (otherCases.length === 0) {
        return {
            ...propertyDescriptorBase,
            ...columnCase,
        };
    } else {
        return {
            ...propertyDescriptorBase,
            cases: columnFirst ? [columnCase, ...otherCases] : [...otherCases, columnCase],
        };
    }
}

interface ColumnPropertyDescriptorFlags<T extends Description> {
    readonly preferredNames?: readonly string[]; // default: `[]`
    readonly preferredType?: ColumnTypePredicate; // default: `"string"`
    readonly ignoreColumnsFromProperties?: readonly string[]; // default: `[]`
    readonly isDefaultCaption?: boolean; // default: `false`
    readonly editable?: boolean; // default: `true`
    readonly searchable?: boolean; // default: `true`
    readonly isEditedInApp?: boolean; // default: `false`
    readonly applyFormat?: boolean; // default: `true`
    readonly allowUserProfileColumns?: boolean; // default: `true`
    readonly emptyByDefault?: boolean; // default: `false`
    // Don't allow picking values from the row to be added in an Add screen.
    // Only used for default value properties right now, which can't come from
    // the row to be added.
    readonly needsExistingValue?: boolean; // default: `false`
    readonly getAllowedColumns?: GetAllowedColumnsFunction<Description>; // default: `getPrimitiveColumns`
    // Only used in properties where the column comes from an indirection, such
    // as with Inline Lists or Relations.
    readonly getIndirectTable?: PropertyTableGetter<T>; // default: `undefined`
}

interface TextPropertyDescriptorFlags<T extends Description> extends ColumnPropertyDescriptorFlags<T> {
    readonly textMenuLabel?: string; // default: `"Custom"`
    readonly isCaption?: string; // default: `undefined`
    readonly isMultiLine?: boolean; // default: `false`
    readonly isImageURL?: boolean; // default: `false`
    readonly propertySection?: SuperPropertySection; // default: `PropertySection.Data`
    readonly subcomponent?: Subcomponent;
    // Only used for legacy properties
    readonly columnFirst?: boolean; // default: `false`
    readonly defaultValue?: string; // default: `undefined`
    readonly allowColumn?: boolean; // default: `true`
}

function makeColumnCase<T extends Description>(
    required: boolean,
    flags: ColumnPropertyDescriptorFlags<T>,
    defaultPreferredType: ColumnTypePredicate
): ColumnPropertyDescriptorCase<T> {
    return {
        kind: PropertyKind.Column,
        required,
        isEditedInApp: flags.isEditedInApp ?? false,
        editable: flags.editable ?? true,
        searchable: flags.searchable ?? true,
        getAllowedColumns: flags.getAllowedColumns ?? getPrimitiveColumns,
        preferredNames: flags.preferredNames ?? [],
        preferredType: flags.preferredType ?? defaultPreferredType,
        isDefaultCaption: flags.isDefaultCaption ?? false,
        applyFormat: flags.applyFormat ?? true,
        emptyByDefault: flags.emptyByDefault ?? false,
        needsExistingValue: flags.needsExistingValue ?? false,
        allowUserProfileColumns: flags.allowUserProfileColumns ?? true,
        ignoreColumnsFromProperties: flags.ignoreColumnsFromProperties ?? [],
        getIndirectTable: flags.getIndirectTable,
    };
}

export function makeTextPropertyDescriptor<T extends Description>(
    property: string | PropertySource<T>,
    label: PropertyLabel,
    placeholder: string,
    required: boolean,
    mutatingScreenKind: MutatingScreenKind | undefined,
    flags: TextPropertyDescriptorFlags<T>,
    otherCases: readonly SpecialValuePropertyDescriptorCase[] = []
): PropertyDescriptor {
    const stringCase: StringPropertyDescriptorCase = {
        kind: PropertyKind.String,
        required,
        isCaption: flags.isCaption,
        placeholder,
        isMultiLine: flags.isMultiLine,
        isImageURL: flags.isImageURL,
        menuLabel: flags.textMenuLabel ?? "Custom",
        defaultValue: flags.defaultValue,
    };
    return defined(
        makeColumnOrLiteralPropertyDescriptor(
            property,
            label,
            mutatingScreenKind,
            flags.propertySection ?? PropertySection.Data,
            flags.subcomponent,
            flags.columnFirst ?? false,
            flags.allowColumn !== false ? makeColumnCase(required, flags, "string") : undefined,
            [stringCase, ...otherCases]
        )
    );
}

export function makeTextPropertyDescriptorWithSpecialValue<T extends Description>(
    property: string | PropertySource<T>,
    label: PropertyLabel,
    placeholder: string,
    required: boolean,
    mutatingScreenKind: MutatingScreenKind | undefined,
    flags: TextPropertyDescriptorFlags<T>
): PropertyDescriptor {
    return makeTextPropertyDescriptor(property, label, placeholder, required, mutatingScreenKind, flags, [
        {
            kind: PropertyKind.SpecialValue,
            withActionSource: false,
            withClearColumn: false,
        },
    ]);
}

// ##makeDefaultValuePropertyDescriptor:
// This makes a property descriptor for default values, which are allowed in
// add and form screens.
export function makeDefaultValuePropertyDescriptor<T extends Description>(
    ccc: AppDescriptionContext,
    mutatingScreenKind: MutatingScreenKind | undefined,
    defaultPreferredType: ColumnTypeKind,
    placeholder: string = "Enter the default value",
    withCustomCase: boolean = true,
    enumOptions?: readonly BasePrimitiveValue[]
): PropertyDescriptor | undefined {
    if (mutatingScreenKind !== MutatingScreenKind.AddScreen && mutatingScreenKind !== MutatingScreenKind.FormScreen) {
        return undefined;
    }

    let columnCase: ColumnPropertyDescriptorCase<T> | undefined;
    if (
        mutatingScreenKind === MutatingScreenKind.FormScreen ||
        definedMap(ccc.appDescription, getUserProfileTableInfo) !== undefined
    ) {
        columnCase = makeColumnCase(
            defaultPreferredType === "boolean",
            {
                searchable: false,
                applyFormat: false,
                emptyByDefault: true,
                needsExistingValue: true,
            },
            defaultPreferredType
        );
    }

    const otherCases: (
        | StringPropertyDescriptorCase
        | NumberPropertyDescriptorCase
        | EnumPropertyDescriptorCase<any, T>
    )[] = [];
    if (enumOptions !== undefined) {
        const enumCases: EnumPropertyCase<any>[] = enumOptions.map(o => ({ label: o.toString(), value: o }));
        if (columnCase === undefined) {
            enumCases.unshift({ label: "—", value: "" });
        }
        otherCases.push({
            kind: PropertyKind.Enum,
            menuLabel: "Default value",
            cases: enumCases,
            defaultCaseValue: enumCases[0]?.value,
            visual: "dropdown",
        });
    } else if (!withCustomCase) {
        // no custom case
    } else if (isNumberTypeKind(defaultPreferredType)) {
        otherCases.push({
            kind: PropertyKind.Number,
            required: RequiredKind.NotRequiredDefaultMissing,
            placeholder,
            defaultValue: 0,
        });
    } else if (defaultPreferredType === "boolean") {
        otherCases.push({
            kind: PropertyKind.Enum,
            menuLabel: "Default value",
            cases: [
                {
                    label: "On",
                    value: true,
                },
                {
                    label: "Off",
                    value: false,
                },
            ],
            defaultCaseValue: false,
            visual: "dropdown",
        });
    } else {
        if (isDateTimeTypeKind(defaultPreferredType)) {
            const enumCase: EnumPropertyDescriptorCase<"now", Description> = {
                kind: PropertyKind.Enum,
                menuLabel: "Default value",
                cases: [
                    {
                        label: defaultPreferredType === "date" ? "Today" : "Now",
                        value: "now",
                    },
                ],
                defaultCaseValue: undefined,
                visual: "dropdown",
            };
            otherCases.push(enumCase);
        }

        otherCases.push({
            kind: PropertyKind.String,
            required: false,
            isCaption: undefined,
            placeholder,
            isMultiLine: false,
            menuLabel: "Custom",
        });
    }

    return makeColumnOrLiteralPropertyDescriptor(
        "defaultValue",
        "Default value",
        mutatingScreenKind,
        PropertySection.Data,
        undefined,
        enumOptions !== undefined,
        columnCase,
        otherCases
    );
}

interface CaptionPropertyDescriptorFlags {
    readonly label?: string; // default: `"Title"`
    readonly placeholder?: string; // default: `"Enter title"`
    readonly propertySection?: SuperPropertySection; // default: `PropertySection.Design`
}

export function makeCaptionStringPropertyDescriptor(
    defaultCaption: string,
    required: boolean,
    mutatingScreenKind: MutatingScreenKind | undefined,
    flags?: CaptionPropertyDescriptorFlags,
    propertyNameOverride?: string
): PropertyDescriptor {
    return makeTextPropertyDescriptor(
        propertyNameOverride ?? "caption",
        flags?.label ?? "Title",
        flags?.placeholder ?? "Enter title",
        required,
        mutatingScreenKind,
        {
            isCaption: defaultCaption,
            preferredNames: ["caption", "title", "name", "description"],
            propertySection: flags?.propertySection ?? PropertySection.Design,
            searchable: false,
        }
    );
}

export function makeNumberPropertyDescriptor<T extends Description>(
    propertyName: string,
    label: PropertyLabel,
    placeholder: string,
    required: RequiredKind,
    defaultValue: number,
    mutatingScreenKind: MutatingScreenKind | undefined,
    flags: ColumnPropertyDescriptorFlags<T>
): PropertyDescriptor {
    const columnCase = makeColumnCase(required === RequiredKind.Required, flags, "number");
    const numberCase: NumberPropertyDescriptorCase = {
        kind: PropertyKind.Number,
        required,
        placeholder,
        defaultValue,
    };

    return defined(
        makeColumnOrLiteralPropertyDescriptor(
            propertyName,
            label,
            mutatingScreenKind,
            PropertySection.Data,
            undefined,
            false,
            columnCase,
            [numberCase]
        )
    );
}

export function makeImagePropertyDescriptor<T extends Description>(
    propertyName: string,
    label: string,
    customMenuLabel: string,
    customPlaceholder: string,
    required: boolean,
    emptyByDefault: boolean | undefined,
    isEditedInApp: boolean,
    withEmoji: boolean,
    withArray: boolean,
    getIndirectTable: PropertyTableGetter<T> | undefined,
    mutatingScreenKind: MutatingScreenKind | undefined,
    propertySection?: SuperPropertySection,
    subcomponent?: Subcomponent
): PropertyDescriptor {
    return makeTextPropertyDescriptor(propertyName, label, customPlaceholder, required, mutatingScreenKind, {
        textMenuLabel: customMenuLabel,
        isEditedInApp,
        searchable: false,
        isImageURL: true,
        emptyByDefault,
        getAllowedColumns: withArray ? getStringTypeOrStringTypeArrayColumns : undefined,
        preferredNames: imageProperties,
        preferredType: !withEmoji && !withArray ? "image-uri" : t => isImageType(t, withEmoji, withArray),
        columnFirst: true,
        getIndirectTable,
        propertySection,
        subcomponent,
    });
}

export function makeTitleSubtitleImagePropertyDescriptors<T extends Description>(
    mutatingScreenKind: MutatingScreenKind | undefined,
    titleRequired: boolean,
    withEmoji: boolean = false,
    getIndirectTable?: PropertyTableGetter<T> | undefined
): readonly PropertyDescriptor[] {
    const titleDescriptor = makeTextPropertyDescriptor(
        "titleProperty",
        "Title",
        "Enter title",
        titleRequired,
        mutatingScreenKind,
        {
            preferredNames: titleProperties,
            columnFirst: true,
            getIndirectTable,
        }
    );
    const subtitleDescriptor = makeTextPropertyDescriptor(
        "subtitleProperty",
        "Details",
        "Enter details",
        false,
        mutatingScreenKind,
        {
            preferredNames: titleProperties,
            columnFirst: true,
            ignoreColumnsFromProperties: ["titleProperty"],
            getIndirectTable,
        }
    );
    const imageDescriptor = makeImagePropertyDescriptor(
        "imageURLProperty",
        "Image",
        "From URL or address",
        "Enter URL or address",
        false,
        false,
        false,
        withEmoji,
        true,
        getIndirectTable,
        mutatingScreenKind
    );
    return [titleDescriptor, subtitleDescriptor, imageDescriptor];
}

export const thisItemEnumPropertyDescriptorCase: EnumPropertyDescriptorCase<true, Description> = {
    kind: PropertyKind.Enum,
    menuLabel: "This item",
    cases: [
        {
            value: true,
            label: "This item",
        },
    ],
    defaultCaseValue: true,
    visual: "dropdown",
};

export function makeTableOrRelationPropertyDescriptor<T extends Description>(
    mutatingScreenKind: MutatingScreenKind | undefined,
    allowSingleRelations: boolean,
    sourceIsDefaultCaption: boolean,
    label: string,
    propertySection: SuperPropertySection,
    allowRewrite: boolean,
    propertySource: PropertySource<T>
): PropertyDescriptor {
    const withColumns = mutatingScreenKind !== MutatingScreenKind.AddScreen;
    const columnCase: PropertyDescriptorCase<T> = {
        kind: PropertyKind.Column,
        required: true,
        editable: true,
        searchable: false,
        isDefaultCaption: sourceIsDefaultCaption,
        getAllowedColumns: t =>
            t.columns.filter(
                c => isMultiRelationType(c.type) || (allowSingleRelations && isSingleRelationType(c.type))
            ),
        getEmptyCase: t => {
            const { errorMessage, linkURL } = componentErrorAndLinkForReference(t.input);
            return {
                label: errorMessage,
                linkURL,
            };
        },
    };
    const tableCase: PropertyDescriptorCase<T> = {
        kind: PropertyKind.Table,
        required: true,
        isDefaultCaption: sourceIsDefaultCaption,
        getAllowedTables: (_desc, schema) => schema.tables,
    };
    const base = {
        property: propertySource as PropertySource<T>,
        label,
        section: propertySection,
        doNotRewrite: !allowRewrite,
    };
    if (withColumns) {
        const cases: PropertyDescriptorCase<T>[] = [];
        cases.push(columnCase);
        if (mutatingScreenKind === undefined && allowSingleRelations) {
            cases.push(thisItemEnumPropertyDescriptorCase);
        }
        cases.push(tableCase);

        return { ...base, cases };
    } else {
        return { ...base, ...tableCase };
    }
}

export function makeSingleRelationOrThisItemPropertyDescriptor(
    propertyName: string,
    label: string,
    section: SuperPropertySection
): PropertyDescriptor {
    return {
        property: { name: propertyName, defaultValue: makeEnumProperty(true) },
        label,
        section,
        cases: [
            thisItemEnumPropertyDescriptorCase,
            new ColumnPropertyHandler(
                propertyName,
                label,
                [ColumnPropertyFlag.Editable, ColumnPropertyFlag.Required],
                undefined,
                undefined,
                t => t.columns.filter(c => isSingleRelationType(c.type) && isColumnNonHidden(c)),
                "table-ref",
                section
            ),
        ],
    };
}

export function getEditedColumnsForProperties(
    properties: readonly PropertyDescriptor[],
    desc: Description,
    tables: InputOutputTables,
    mutatingScreenKind: MutatingScreenKind | undefined,
    ccc: AppDescriptionContext,
    withActions: boolean
): EditedColumnsAndTables {
    let results: EditedColumnsAndTables = emptyEditedColumnsAndTables;
    for (const propertyDescr of properties) {
        const propertyAndDescriptor = getPropertyAndDescriptorFromDescription(desc, propertyDescr);
        if (propertyAndDescriptor === undefined) continue;

        const { description: pd, descriptorCase: pdc } = propertyAndDescriptor;

        const handler = handlerForPropertyKind(pdc.kind);
        results = combineEditedColumnsAndTables(
            results,
            handler.getEditedColumns(pdc, pd, desc, tables, mutatingScreenKind, ccc, withActions)
        );
    }
    return results;
}

export function makePlaceholderPropertyDescriptor(
    mutatingScreenKind: MutatingScreenKind | undefined,
    propertySection: SuperPropertySection,
    placeholderPlaceholder: string = "Enter placeholder"
): PropertyDescriptor {
    return makeTextPropertyDescriptor("placeholder", "Placeholder", placeholderPlaceholder, true, mutatingScreenKind, {
        searchable: false,
        propertySection,
    });
}

export function makeDefaultAction<T extends Description>(
    descriptor: ActionPropertyDescriptorCase<T>,
    tables: InputOutputTables,
    desc: T
): ActionDescription | undefined {
    const { kinds, defaultAction } = descriptor;
    if (defaultAction === true) {
        assert(kinds.length > 0);
        return { kind: kinds[0] };
    } else if (typeof defaultAction === "function") {
        return defaultAction(tables.input, desc);
    }
    return undefined;
}

export function makeDynamicFilterColumnPropertyHandler<T extends Description>(
    getIndirectTable: PropertyTableGetter<T> | undefined
) {
    return new ColumnPropertyHandler(
        "dynamicFilterColumn",
        "Filter by",
        [ColumnPropertyFlag.Editable, ColumnPropertyFlag.EmptyByDefault, ColumnPropertyFlag.Optional],
        getIndirectTable,
        ["category", "kind"],
        getPrimitiveOrPrimitiveArrayNonHiddenColumns,
        "string",
        PropertySection.InAppFilterData
    );
}

export const dynamicFilterColumnPropertyHandler = makeDynamicFilterColumnPropertyHandler(undefined);

export function makeScreenTitlePropertyDescriptor(
    mutatingScreenKind: MutatingScreenKind | undefined,
    placeholder: string = "Screen title"
): PropertyDescriptor {
    return makeTextPropertyDescriptor("title", "Title", placeholder, true, mutatingScreenKind, {
        preferredNames: titleProperties,
        propertySection: PropertySection.Design,
        searchable: false,
    });
}
